import { inject, Pipe, PipeTransform } from '@angular/core';
import { HeadingTag } from '@shared/components/title/models';
import { LogsService } from '@shared/services/logs';

/** Precompiled regex for token parsing: [[...]]. Non-greedy, supports multiple occurrences per line. */
const TOKEN_RE = /\[\[(.+?)\]\]/g;

/** Robust line-splitting: supports "\\", newlines and <br> (case-insensitive, with or without "/"). */
const LINE_SPLIT_RE = /\\\\|\r?\n|<br\s*\/?>/i;

/** Small LRU-ish cache size for memoization. */
const CACHE_MAX = 200;

const TITLE_CLASS_MAP: Readonly<Record<HeadingTag, string>> = {
  h1: 'title-h1',
  h2: 'title-h2',
  h3: 'title-h3',
  h4: 'title-h4',
  h5: '',
  h6: '',
  span: '',
  'custom-15': 'title-custom-15',
  'custom-18-35': 'title-custom-18-35',
  'custom-20-22': 'title-custom-20-22',
  'custom-24-35': 'title-custom-24-35',
  'custom-35': 'title-custom-35',
  'custom-30': 'title-custom-30',
  'custom-35-45': 'title-custom-35-45',
  'custom-35-device-agnostic': 'title-custom-35-device-agnostic',
  'custom-47': 'title-custom-47',
  'custom-40': 'title-custom-40',
  'custom-40-60': 'title-custom-40-60',
  'custom-25': 'title-custom-25',
  'custom-30-35': 'title-custom-30-35',
  normal: 'title-custom-normal',
} as const;

const TITLE_ALT_CLASS_MAP: Readonly<Record<HeadingTag, string>> = {
  h1: 'title-h1-alt',
  h2: 'title-h2-alt',
  h3: 'title-h3-alt',
  h4: '',
  h5: '',
  h6: '',
  span: '',
  'custom-15': 'title-custom-15-alt',
  'custom-18-35': 'title-custom-18-35-alt',
  'custom-20-22': 'title-custom-20-22-alt',
  'custom-24-35': 'title-custom-24-35-alt',
  'custom-30': 'title-custom-30-alt',
  'custom-35': 'title-custom-35-alt',
  'custom-35-45': 'title-custom-35-45-alt',
  'custom-35-device-agnostic': 'title-custom-35-device-agnostic-alt',
  'custom-47': 'title-custom-47-alt',
  'custom-40': 'title-custom-40-alt',
  'custom-40-60': 'title-custom-40-60-alt',
  'custom-25': 'title-custom-25-alt',
  'custom-30-35': 'title-custom-30-35-alt',
  normal: 'title-custom-normal-alt',
} as const;

/** Escapes HTML special characters to prevent HTML injection/XSS in user content. */
function escapeHtml(value: string): string {
  if (!/[&<>"'`]/.test(value)) return value;
  return value
    .replaceAll(/&/g, '&amp;')
    .replaceAll(/</g, '&lt;')
    .replaceAll(/>/g, '&gt;')
    .replaceAll(/"/g, '&quot;')
    .replaceAll(/'/g, '&#39;')
    .replaceAll(/`/g, '&#96;');
}

/** Builds a class attribute only when non-empty, to avoid `class=""`. */
function classAttr(cls: string): string {
  return cls ? ` class="${cls}"` : '';
}

/**
 * Wraps inner HTML into a block container span.
 * Keeping only the "block" class on the wrapper ensures consistent layout,
 * while typography classes are applied on child spans.
 */
function wrapBlock(innerHtml: string): string {
  return `<span class="block">${innerHtml}</span>`;
}

/**
 * Builds a span with a class and escaped textual content.
 */
function spanFragment(cls: string, text: string): string {
  if (!text) return '';
  return `<span${classAttr(cls)}>${escapeHtml(text)}</span>`;
}

/**
 * Normalize unknown error shapes to a readable object.
 */
function normalizeError(err: unknown): { name?: string; message?: string } {
  if (err instanceof Error) return { name: err.name, message: err.message };
  return err as { name?: string; message?: string };
}

/**
 * A custom Angular Pipe that transforms text into styled HTML titles with emphasis tokens.
 * It supports memoization for performance optimization and allows customization of the heading style.
 *
 * The pipe processes the input text to format it with specific styles based on the provided heading level
 * and applies alternate styles for segments enclosed in `[[...]]` tokens.
 *
 * @example
 * <!-- In an Angular template -->
 * <h1 [innerHTML]="'Some [[emphasized]] Text' | titleStyle:'h1'"></h1>
 *
 * <!-- Output -->
 * <h1>
 *   <span class="title-class">Some </span>
 *   <span class="title-alt-class">emphasized</span>
 *   <span class="title-class"> Text</span>
 * </h1>
 */
@Pipe({
  name: 'titleStyle',
})
export class TitleStylePipe implements PipeTransform {
  private readonly logs = inject(LogsService);
  private readonly cache = new Map<string, string>();

  transform(text: unknown, as: HeadingTag): string {
    try {
      if (typeof text !== 'string') {
        this.logs.error('TitleStylePipe', 'Invalid input type', { receivedType: typeof text, text, as });
        return '';
      }

      const cacheKey = `${as}::${text}`;
      const cached = this.cache.get(cacheKey);
      if (cached !== undefined) {
        return cached;
      }

      const html = this.transformText(text, as);

      if (this.cache.size >= CACHE_MAX) {
        const firstKey = this.cache.keys().next().value;
        if (firstKey) this.cache.delete(firstKey);
      }

      this.cache.set(cacheKey, html);
      this.logs.trace('TitleStylePipe', 'Transform', { origin: text, result: html, as });

      return html;
    } catch (err) {
      this.logs.error('TitleStylePipe', 'Failed to transform text', { error: normalizeError(err), text, as });
      return typeof text === 'string' ? text : '';
    }
  }

  /**
   * Internal transformation:
   * - Splits into lines
   * - Parses emphasis tokens per line
   * - Emits a small, flat HTML string
   */
  private transformText(text: string, as: HeadingTag): string {
    const { title, titleAlt } = this.getTitleAltSize(as);
    const lines = text.split(LINE_SPLIT_RE);
    const parsed = lines.map((line) => this.transformLine(line, title, titleAlt));
    return parsed.join('\n');
  }

  /**
   * Compute classes for the "title" and its "alt" emphasis counterpart.
   * Unknown keys gracefully fallback to empty classes.
   */
  private getTitleAltSize(as: HeadingTag): { title: string; titleAlt: string } {
    return {
      title: TITLE_CLASS_MAP[as] ?? '',
      titleAlt: TITLE_ALT_CLASS_MAP[as] ?? '',
    };
  }

  /** Transform a single line, applying alt styles for [[...]] segments. */
  private transformLine(line: string, titleCls: string, altCls: string): string {
    if (!line) {
      // Preserve an empty block to keep vertical rhythm consistent.
      return wrapBlock('');
    }

    // Fast path: no tokens -> wrap once and return.
    if (!line.includes('[[')) {
      return wrapBlock(spanFragment(titleCls, line));
    }

    // Tokenized path: iterate matches and build fragments.
    let out = '';
    let lastIndex = 0;
    TOKEN_RE.lastIndex = 0; // Important: reset global regex state.

    for (let match: RegExpExecArray | null = TOKEN_RE.exec(line); match !== null; match = TOKEN_RE.exec(line)) {
      const before = line.slice(lastIndex, match.index);
      if (before) out += spanFragment(titleCls, before);

      // match[1] is the captured emphasized content.
      out += spanFragment(altCls, match[1]!);

      lastIndex = match.index + match[0].length;
    }

    // Remainder (if any) after the last token.
    if (lastIndex < line.length) {
      out += spanFragment(titleCls, line.slice(lastIndex));
    }

    return wrapBlock(out);
  }
}
